import React, { FC, useCallback } from 'react'
import { styled } from '@moonpig/launchpad-utils'
import { Box, SideModal } from '@moonpig/launchpad-components'
import { SortOrder } from '@moonpig/web-explore-types-graphql'
import {
  createGenericSelectContentEvent,
  trackGAEvent,
} from '@moonpig/web-core-analytics'
import { useExperiment } from '@moonpig/web-core-eppo/browser'
import {
  DESKTOP_MODAL_FILTERS_MENU_WIDTH,
  FILTERS_MENU_OFFSET,
  FILTERS_MENU_RESULTS_CTA_CONTAINER_HEIGHT,
} from '../../constants'
import {
  Header,
  SlidingMenu,
  FilterCategorySubMenu,
  SortBy,
  ResultsButton,
  AccordionMenu,
} from './components'
import { FilterCategory, FilterItem } from '../../services/types/services'
import { mapFilterItem } from '../../utils/GalleryFiltering/mappings'
import { useSearchStore } from '../../store/SearchStore'
import { useFindLocaleText } from '../../text-localisation'
import { FilterSource } from '../../types'
import { shouldShowClearButton } from '../../utils/GalleryFiltering/shouldShowClearButton'
import {
  RangeFilterInput,
  useRangeFilterInput,
  RangeFieldTypes,
} from '../RangeFilterInput'

const StyledOffsetContainer = styled(Box)`
  margin-top: ${FILTERS_MENU_OFFSET}px;
  height: 100%;
  max-height: calc(
    100vh - ${FILTERS_MENU_OFFSET + FILTERS_MENU_RESULTS_CTA_CONTAINER_HEIGHT}px
  );
  overflow: hidden auto;
  overscroll-behavior: contain;

  @supports (max-height: 100dvh) {
    max-height: calc(
      100dvh -
        ${FILTERS_MENU_OFFSET + FILTERS_MENU_RESULTS_CTA_CONTAINER_HEIGHT}px
    );
  }
`

const StyledScrollableArea = styled(Box)`
  position: relative;
  overflow: hidden auto;
  overscroll-behavior: contain;
  min-height: calc(
    100vh - ${FILTERS_MENU_OFFSET + FILTERS_MENU_RESULTS_CTA_CONTAINER_HEIGHT}px
  );

  @supports (min-height: 100dvh) {
    min-height: calc(
      100dvh -
        ${FILTERS_MENU_OFFSET + FILTERS_MENU_RESULTS_CTA_CONTAINER_HEIGHT}px
    );
  }
`

const hasSubCategories = (category: FilterCategory) => {
  return category.children.some(child => child.__typename === 'FilterCategory')
}

type FiltersMenuProps = {
  sortOptions: { label: string; value: string }[]
}

export const FiltersMenu: FC<FiltersMenuProps> = ({ sortOptions }) => {
  const localiseText = useFindLocaleText()
  const isOpen = useSearchStore(store => store.filtersMenuOpen)
  const pageContext = useSearchStore(store => store.pageContext)
  const filters = useSearchStore(store => store.filters)
  const toggleFiltersMenu: () => void = useSearchStore(
    store => store.toggleFiltersMenu,
  )
  const toggleFiltersCategory = useSearchStore(
    store => store.toggleFiltersCategory,
  )
  const onRangeFilterChange = useSearchStore(store => store.onRangeFilterChange)
  const rangeFilters = useSearchStore(store => store.rangeFilters)
  const categories = useSearchStore(store => store.selectedCategories)
  const toggleFilter = useSearchStore(store => store.toggleFilter)
  const applyRangeFilter = useSearchStore(store => store.applyRangeFilter)
  const resultsLoading = useSearchStore(store => store.loading)
  const resultsCount = useSearchStore(store => store.results.totalCount)
  const sortBy = useSearchStore(store => store.sortBy)
  const sortValue = useSearchStore(store => store.sortValue)
  const defaultSortValue = useSearchStore(store => store.initialSortValue)
  const sortMenuOpen = useSearchStore(store => store.sortMenuOpen)
  const selectedFilters = useSearchStore(store => store.selectedFilters)

  const useAgeSelectionUx =
    useExperiment('search-use-age-selection-ux', {
      fallback: 'control',
    })?.toLowerCase() === 'enabled'

  const { createConfiguration, isRangeFilterValueValid } = useRangeFilterInput()
  const rangeFilterValue = rangeFilters.find(
    f => f.name === categories[categories.length - 1]?.id,
  )?.value

  const applyAgeFilter = (filter: FilterCategory) => {
    applyRangeFilter({
      filter,
      value: rangeFilterValue || /* istanbul ignore next */ '',
      source: FilterSource.FiltersMenu,
    })
    toggleFiltersMenu()
  }

  const filterItems = ({
    category,
    index,
  }: {
    category: FilterCategory
    index: number
  }) => {
    return useAgeSelectionUx && category.useAgeInputField ? (
      <Box p={6}>
        <RangeFilterInput
          onChange={(ageInput: string) =>
            onRangeFilterChange({ filterId: category.id, value: ageInput })
          }
          input={createConfiguration(RangeFieldTypes.age, rangeFilterValue)}
          hasValidValue={isRangeFilterValueValid(
            RangeFieldTypes.age,
            rangeFilterValue,
          )}
        />
      </Box>
    ) : (
      category.children.map(filterItem =>
        mapFilterItem({
          filterItem,
          onClick: onFilterItemClick,
          level: index + 1,
        }),
      )
    )
  }

  const hideTopLevelClearButton = !shouldShowClearButton({
    selectedFilters,
    sortValue,
    defaultSortValue,
  })

  const onFilterItemClick = useCallback(
    (filterItem: FilterItem, select?: boolean) => {
      switch (filterItem.__typename) {
        case 'FilterCategory':
          if (select === undefined) {
            toggleFiltersCategory(filterItem)
          } else {
            toggleFilter({
              filter: filterItem,
              select: !!select,
              source: FilterSource.FiltersMenu,
            })
          }
          break
        case 'Filter':
          toggleFilter({
            filter: filterItem,
            select: !!select,
            source: FilterSource.FiltersMenu,
          })
          break
        case 'FilterToggle':
        case 'FilterInvertedToggle':
          toggleFilter({
            filter: filterItem,
            select: !!select,
            source: FilterSource.Toggle,
          })
          break
      }
    },
    [toggleFilter, toggleFiltersCategory],
  )

  const onSortByChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      sortBy(event.target.value as SortOrder)
    },
    [sortBy],
  )

  const isAgeInputFilterCategory =
    useAgeSelectionUx &&
    categories.length > 0 &&
    categories[categories.length - 1].useAgeInputField

  const ResultsCTA = (
    <Box borderTop={1} borderColor="colorBorder03">
      <ResultsButton
        onClick={
          isAgeInputFilterCategory
            ? () => applyAgeFilter(categories[categories.length - 1])
            : toggleFiltersMenu
        }
        loading={resultsLoading}
        resultsCount={resultsCount}
        label={
          isAgeInputFilterCategory
            ? localiseText('find.age_input.apply')
            : undefined
        }
        disabled={
          useAgeSelectionUx &&
          isAgeInputFilterCategory &&
          (rangeFilterValue === undefined ||
            !isRangeFilterValueValid(RangeFieldTypes.age, rangeFilterValue))
        }
      />
    </Box>
  )

  return isOpen ? (
    <SideModal
      onDismiss={toggleFiltersMenu}
      isOpen={isOpen}
      description={localiseText('find.filter_menu')}
      side="left"
      maxWidth={{ md: DESKTOP_MODAL_FILTERS_MENU_WIDTH }}
      minWidth={{ md: DESKTOP_MODAL_FILTERS_MENU_WIDTH }}
    >
      <SlidingMenu>
        <Header
          onClose={toggleFiltersMenu}
          hideClearButton={hideTopLevelClearButton}
        />
        <StyledOffsetContainer>
          <SortBy
            options={sortOptions}
            value={sortValue}
            onChange={onSortByChange}
            defaultValue={defaultSortValue}
            open={sortMenuOpen}
            resultsButton={ResultsCTA}
          />
          {filters.map(filterItem =>
            mapFilterItem({ filterItem, onClick: onFilterItemClick }),
          )}
        </StyledOffsetContainer>

        {categories.map((category, index) => (
          <FilterCategorySubMenu
            key={`${category.id}-sub-menu`}
            category={category}
            level={index + 1}
            onClose={() => {
              toggleFiltersCategory(category)
            }}
            hideClearButton={
              !shouldShowClearButton({
                category,
                selectedFilters,
              })
            }
          >
            <StyledScrollableArea>
              {hasSubCategories(category) ? (
                <AccordionMenu
                  category={category}
                  level={index + 1}
                  onClickItem={onFilterItemClick}
                  onClickCategoryItem={({ action, label }) => {
                    trackGAEvent(
                      createGenericSelectContentEvent({
                        eventCategory: `${pageContext.type} action`,
                        eventAction: action,
                        eventLabel: label,
                      }),
                    )
                  }}
                />
              ) : (
                filterItems({ category, index })
              )}
            </StyledScrollableArea>
            {ResultsCTA}
          </FilterCategorySubMenu>
        ))}
        {ResultsCTA}
      </SlidingMenu>
    </SideModal>
  ) : null
}
